import React, { useMemo, useState } from 'react';
import * as Sentry from '@sentry/react';
import { useNavigate } from 'react-router-dom';

import { ScenarioPhotoLightboxByLegPhase, type ScenarioLegPhotoTarget } from '@components/ScenarioPhotoLightbox';
import { useScenarioActions } from '../hooks/useScenarioActions';
import { useOrderAssignmentSettling } from '../hooks/useOrderAssignmentSettling';
import {
  ScenarioActionsContext,
  type ScenarioActionsContextValue,
  type ScenarioOrderHandlers,
} from './scenarioActionsContext';
import { usePortalGuestChat, usePortalMoveForActions } from '../portalComponents';
import AssignOrUnassignMoveModal from '@components/Modals/AssignOrUnassignMoveModal';
import {
  CancelLiteModal,
  MoveCancelModal,
  MoveTimeModal,
} from '@hopdrive/feature-move-actions';
import type { ScenarioAssignmentAction, ScenarioView } from '@hopdrive/feature-order-index';
import siteConfig from '@config';
import { useUser } from '@store';
import { movePath, planMovesPath } from '@features/products/productRoutes';

/**
 * The interaction layer every scenario surface shares.
 *
 * One provider owns what happens when an order is acted on — the photo viewer, the portal's
 * assign / change-time / cancel dialogs and the on-open fetch that feeds them, guest chat,
 * and navigation — so a page of twenty-five orders mounts ONE of each, and a card is a pure
 * renderer handed callbacks.
 *
 * **The mutating dialogs are the portal's own, unchanged.** Each takes a whole `Moves`
 * object and resolves its pair itself, so the subject move is fetched when a dialog opens
 * rather than carried on every card. The subject is always the order's first drive leg by
 * cardinality.
 *
 * Chat degrades rather than crashes: where the chat provider is absent (outside the app
 * layout), the chat callback becomes a no-op and the card's own gating hides the button.
 */
type ScenarioDialogKind = 'assign' | 'changeTime' | 'cancel';

interface ScenarioDialogTarget {
  kind: ScenarioDialogKind;
  moveId: number;
  scenarioId: number;
}

export const ScenarioActionsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const navigate = useNavigate();
  const user = useUser();
  const { openChat } = usePortalGuestChat();

  const [photoTarget, setPhotoTarget] = useState<ScenarioLegPhotoTarget | null>(null);
  const [dialogTarget, setDialogTarget] = useState<ScenarioDialogTarget | null>(null);

  const { move: dialogMove, offers: dialogOffers } = usePortalMoveForActions(dialogTarget?.moveId ?? null);
  const closeDialog = () => setDialogTarget(null);

  const { assigningScenarioIds, beginSettling } = useOrderAssignmentSettling();

  // The assign modal's close IS the assignment event: an output means its mutation
  // succeeded, and the order it names holds an assigning pill until the data agrees.
  const handleAssignModalClose = (output?: { action?: ScenarioAssignmentAction; driverId?: number | null }) => {
    const target = dialogTarget;
    closeDialog();
    if (!target || !output?.action) return;
    beginSettling({ scenarioId: target.scenarioId, action: output.action, driverId: output.driverId ?? null });
  };

  const value = useMemo<ScenarioActionsContextValue>(() => {
    const openDialog = (view: ScenarioView, kind: ScenarioDialogKind) => {
      const subjectMoveId = view.actions.openTargets[0];
      if (subjectMoveId !== undefined) setDialogTarget({ kind, moveId: subjectMoveId, scenarioId: view.scenarioId });
    };

    /**
     * The planner takes the order's drive legs directly — the one action where the order
     * model replaces a parent/child reconstruction outright.
     */
    const openPlanner = (view: ScenarioView, inverse: boolean) => {
      const [moveId1, moveId2] = view.actions.openTargets;
      navigate(planMovesPath(), {
        state: { moveId1, inverse, ...(moveId2 && moveId2 !== moveId1 ? { moveId2 } : {}) },
      });
    };

    const buildCardActions = (view: ScenarioView): ScenarioOrderHandlers => ({
      onAssignDriver: () => openDialog(view, 'assign'),
      onOpenMove: (moveId: number) => navigate(movePath(moveId)),
      onOpenChat: () => {
        if (view.actions.chatMoveId !== null) openChat?.(view.actions.chatMoveId);
      },
      onCreateDuplicate: () => openPlanner(view, false),
      onCreateInverse: () => openPlanner(view, true),
      onChangePromiseTime: () => openDialog(view, 'changeTime'),
      onCancelOrder: () => openDialog(view, 'cancel'),
    });

    return {
      openLegPhotos: (moveId, phase) => setPhotoTarget({ moveId, phase }),
      buildCardActions,
      assigningScenarioIds,
    };
  }, [navigate, openChat, assigningScenarioIds]);

  // The cancel flow differs by tier, exactly as move details routes it: SaaS cancels
  // outright, Full Service raises a request for dispatcher review.
  const isCancelOpen = dialogTarget?.kind === 'cancel' && !!dialogMove;
  const isLiteMove = dialogMove?.lite === true;

  return (
    <ScenarioActionsContext.Provider value={value}>
      {children}

      <ScenarioPhotoLightboxByLegPhase target={photoTarget} onClose={() => setPhotoTarget(null)} />

      <AssignOrUnassignMoveModal
        open={dialogTarget?.kind === 'assign' && !!dialogMove}
        onClose={handleAssignModalClose}
        moveInput={dialogMove}
        offersInput={dialogOffers}
      />

      <MoveTimeModal
        open={dialogTarget?.kind === 'changeTime' && !!dialogMove}
        onClose={closeDialog}
        moveInput={dialogMove ?? undefined}
        actor={user?.profile?.email}
      />

      {isLiteMove ? (
        <CancelLiteModal
          open={isCancelOpen}
          onClose={closeDialog}
          moveInput={dialogMove}
          actor={user?.profile?.email}
          onCreateDuplicate={state => navigate(planMovesPath(), { state })}
          onError={err => Sentry.captureException(err)}
        />
      ) : (
        <MoveCancelModal
          open={isCancelOpen}
          onClose={closeDialog}
          moveInput={dialogMove}
          adminUrl={siteConfig.hopdrive.admin.url}
          getAuthToken={async () => user?.getToken()}
          onError={err => Sentry.captureException(err)}
        />
      )}
    </ScenarioActionsContext.Provider>
  );
};

/**
 * Mounts a provider only where none exists above, so a fetcher can guarantee working
 * actions without a page-level provider ending up doubled — two mounted providers would
 * mean two photo viewers and two of every dialog.
 */
export const ScenarioActionsHost: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const existing = useScenarioActions();
  if (existing) return <>{children}</>;
  return <ScenarioActionsProvider>{children}</ScenarioActionsProvider>;
};
